import GameEvents from "../../src/const/GameEvents";
import App from "../App";
import { Point, Sprite, Container, DisplayObject, Rectangle, Graphics, Texture, Text } from "pixi.js";
import GameModel from "../model/GameModel";
export type PIXIElement = Sprite | Container | Text | DisplayObject ;

export enum AssetSize {
    FHD = 1920,
    HD = 1280
}

/**
 * Used to position and size graphical elements. The basic (default) coordinate system is rect.
 */
class Layout {

    public rect: Rectangle;
    public portraitRect: Rectangle;
    public hAlignLeft = this.horizontalAlignLeft;
    public hAlignCent = this.horizontalAlignCenter;
    public hAlignRight = this.horizontalAlignRight;
    public vAlignBot = this.verticalAlignBottom;
    public vAlignMid = this.verticalAlignMiddle;
    public vAlignTop = this.verticalAlignTop;
    public deviceHeight: number;
    public deviceWidth: number;
    public stage: Container;
    public startW: number;
    public startH: number;
    public canvasWidth: number;
    public canvasHeight: number;
    public touchStartX = 0;
    public touchStartY = 0;
    public touchEndX = 0;
    public touchEndY = 0;
    public landscape: boolean;
    public fullRect: Rectangle;
    public fullPortraitRect: Rectangle;
    public globalScale: number = 1;
    public assetSize: AssetSize;
    public portraitMultiplier: number;

    constructor() {
    }

    public init(w: number, h: number) {
        this.stage = App.instance.stage;
        this.startW = w;
        this.startH = h;
        this.calculateGlobalScaleAndSize();
        this.rect = new Rectangle(0, 0, w, h);
        this.fullRect = new Rectangle(0, 0, App.width, App.height);
        // this.fullPortraitRect = new Rectangle(0, 0, App.width, App.height);
        this.portraitMultiplier = 1;
        this.fullPortraitRect = new Rectangle(0, 0, App.height * this.portraitMultiplier, App.width * this.portraitMultiplier);
        this.canvasWidth = App.instance.view.width / App.instance.renderer.resolution;
        this.canvasHeight = App.instance.view.height / App.instance.renderer.resolution;
    }

    /**
     * Sets target's X coordinate with applied global scale. Passed value should be relative to origin resolution(1920x1080)
     * @param target
     * @param x
     */
    public setX(target: DisplayObject, x: number) {
        target.x = x * this.globalScale;
    }

    /**
     * Sets target's Y coordinate with applied global scale. Passed value should be relative to origin resolution(1920x1080)
     * @param target
     * @param y
     */
    public setY(target: DisplayObject, y: number) {
        target.y = y * this.globalScale;
    }

    /**
     * Returns the X coordinate of target object relative to origin resolution(1920x1080)
     * @param target
     */
    public getX(target): number {
        return target.x / this.globalScale;
    }

    /**
     * Returns the Y coordinate of target object relative to origin resolution(1920x1080)
     * @param target
     */
    public getY(target): number {
        return target.y / this.globalScale;
    }

    public testSetWidth(target, width, lockScale = true) {
        target.width = width * this.globalScale;
        if (target.scale && lockScale) {
            target.scale.y = target.scale.x;
        }
    }

    public testSetHeight(target, height, lockScale = true) {
        target.height = height * this.globalScale;
        if (target.scale && lockScale) {
            target.scale.x = target.scale.y;
        }
    }

    /**
     * Returns the pixelCount in current scale of the canvas.
     * Typical usage is if you want to tween an objects position. The target X or Y should be with global scale applied.
     * @param pixelCount
     */
    public scalePixels(pixelCount: number): number {
        return pixelCount * this.globalScale;
    }

    public scalePoint(point: Point): Point {
        return new Point(point.x * this.globalScale, point.y * this.globalScale);
    }

    /**
     * Returns the original width of target without the applied globalScale
     * @param target
     */
    public getWidth(target): number {
        return target.width / this.globalScale;
    }

    /**
     * Returns the original height of target without the applied globalScale
     * @param target
     */
    public getHeight(target): number {
        return target.height / this.globalScale;
    }

    /**
     * Calculates globalScale according to initial screen resolution.
     * globalScale is then used in every calculation regarding position and size of display objects.
     */
    protected calculateGlobalScaleAndSize() {
        let biggerValue = this.startW > this.startH ? this.startW : this.startH;
        this.globalScale = biggerValue / 1920;
        // 1920 / 1080 = 1.77 - FHD coefficient
        // if (!this.isLandscape()) {
        //     this.globalScale *= 1.77
        // }

        if (biggerValue >= 1920) {
            this.assetSize = AssetSize.FHD;
        } else {
            this.assetSize = AssetSize.HD;
        }

    }

    /**
     * Is the device in landscape mode ?
     * @public
     * @return {boolean}
     */
    public isLandscape() {
        return this.landscape;
    }


    /**
     * Execute recalculations needed when screen is resized.
     * @public
     * @param w {Number} width
     * @param h {Number} height
     */
    public onResize(w: number, h: number) {
        this.deviceWidth = w;
        this.deviceHeight = h;
        let newOrientation = w > h;

        if (!GameModel.isMobile()) {
            newOrientation = true;
        }

        if (this.landscape) {
            this.portraitRect = new Rectangle(0, 0, this.startW, this.startW * this.startW / this.startH);
        } else {
            this.portraitRect = new Rectangle(0, 0, this.startW, this.startW * h / w);
        }

        let orientationChanged = false;
        this.canvasWidth = App.instance.view.width / App.instance.renderer.resolution;
        this.canvasHeight = App.instance.view.height / App.instance.renderer.resolution;

        if (newOrientation != this.landscape) {
            this.landscape = newOrientation;
            this.calculateGlobalScaleAndSize();
            orientationChanged = true;

        }

        if (GameModel.isMobile(true) != GameModel.isMobile()) {
            this.calculateGlobalScaleAndSize();
        }

        if (App.dispatcher) {
            App.dispatcher.emit(GameEvents.RESIZE, orientationChanged);
        }
    }


    public isInGameBounds(object: Container | Rectangle) {
        return object.x.toFixed(2) >= this.portraitRect.x.toFixed(2) &&
            object.y.toFixed(2) <= this.portraitRect.x.toFixed(2) &&
            object.y.toFixed(2) >= this.portraitRect.y.toFixed(2) &&
            object.y.toFixed(2) <= this.portraitRect.y.toFixed(2);
    }


    /**
     * Set position of a target object relative to a source
     * @public
     * @param target - display object
     * @param x - x position relative to origin resolution (1920x1080)
     * @param y - y position relative to origin resolution (1920x1080)
     */

    public setPos(target: Container, x: number, y: number) {
        // this.horizontalAlignLeft(target, x, src);
        // this.verticalAlignTop(target, y, src);
        this.setX(target, x);
        this.setY(target, y);
    }

    /**
     * Calculates a percentage of the width of the source
     * @public
     * @param w - width (in percent)
     * @param src - either a display object or a rectangle
     * @returns {number}
     */

    // TODO refactor to relativeX
    public relWidth(w: number, src?: { width: number, height: number }) {

        if (src == undefined) {
            src = this.rect;
        }

        return w * src.width / 100;
    }


    /**
     * Calculates a percentage of the height of the source
     * @public
     * @param h - width (in percent)
     * @param src - either a display object or a rectangle
     * @returns {number}
     */

    // TODO refactor to relativeY
    public relHeight(h: number, src?: { width: number, height: number }) {
        if (src == undefined) {
            src = this.rect;
        }

        return h * src.height / 100;
    }

    /**
     * Calculates what percentage of the width of the source is a given width
     * @public
     * @param w - width
     * @param src - either a display object or a rectangle
     * @returns {number}
     */
    public percentWidth(w: number, src?: { width: number, height: number }) {
        if (src == undefined) {
            src = this.fullRect;
        }
        return (w / src.width) * 100;
    }

    /**
     * Calculates what percentage of the height of the source is a given height
     * @public
     * @param h - height
     * @param src - either a display object or a rectangle
     * @returns {number}
     */
    public percentHeight(h: number, src?: { width: number, height: number }) {
        if (src == undefined) {
            src = this.fullRect;
        }
        return (h / src.height) * 100;
    }

    /**
     * Scales object to percentage of parent's width
     * @public
     * @param target - display object
     * @param scale - required scale in percent
     * @param src - display object or rectangle
     */
    // TODO refactor to setWidth
    public setWidth(target: Container, scale: number, src?: Container | Rectangle) {
        if (src == undefined) {
            src = this.rect;
        }

        const requiredScale = src.width * scale / 100 / target.width * target.scale.x;
        target.scale.x = requiredScale;
        target.scale.y = requiredScale;
    }


    /**
     * Scales object to percentage of parent's height
     * @public
     * @param target - display object
     * @param scale - required scale in percent
     * @param src - display object or rectangle
     */
    // TODO refactor to setHeight
    public setHeight(target: Container, scale: number, src?: Container | Rectangle) {
        if (src == undefined) {
            src = this.rect;
        }

        const requiredScale = src.height * scale / 100 / target.height * target.scale.y;
        target.scale.x = requiredScale;
        target.scale.y = requiredScale;
    }

    /**
     * Horizontally align left a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in pixels
     * @param src - display object or rectangle
     */
    public horizontalAlignLeft(target: Container, distance: number = 0, src?: Container | Rectangle) {
        // if (src == undefined) {
        //     src = this.rect;
        // }
        // target.x = src.x + src.width * distance / 100;
        if (src) {
            target.x = this.scalePixels(this.getX(src) + this.getWidth(src) * distance);
        } else {
            target.x = this.scalePixels(distance);
        }
    }


    /**
     * Horizontally align right a target relative to a source
     * @public
     * @param target {DisplayObject} display object
     * @param distance {Number} distance in pixels
     * @param src {DisplayObject} display object or rectangle
     */
    public horizontalAlignRight(target: Container, distance: number = 0, src?: Container | Rectangle) {
        if (src == undefined) {
            src = this.rect;
        }
        if (src) {
            target.x = this.scalePixels(this.getX(src) + this.getWidth(src) - this.getWidth(target) - distance);
            // target.x = src.x + src.width - target.width - this.scalePixels(distance);
        } else {
            target.x = this.scalePixels(1920 - this.getWidth(target) - distance);
        }
        // target.x = src.x + src.width * (100 - distance) / 100 - target.width;
    }


    /**
     * Horizontally align center a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in pixels
     * @param src - display object or rectangle
     */
    public horizontalAlignCenter(target: Sprite | Container | Graphics, distance: number = 0, src?: Sprite | Container | Rectangle) {
        // if (src == undefined) {
        //     src = this.rect;
        // } else if (src == target.parent) {
        //     const width = src.width / src.scale.x;
        //     const height = src.height / src.scale.y;
        //     const x = src instanceof Sprite ? src.anchor.x * width : 0;
        //     const y = src instanceof Sprite ? src.anchor.y * height : 0;
        //     src = new Rectangle(x, y, width, height);
        // }
        //
        // const taX = target instanceof Sprite ? target.anchor.x : 0;
        // const saX = src instanceof Sprite ? src.anchor.x * 100 : 0;
        //
        // target.x = src.x + src.width * (50 + distance - saX) / 100 - target.width * (.5 - taX);
        //
        if (src) {
            target.x = this.scalePixels(this.getX(src) + distance + (this.getWidth(src) - this.getWidth(target)) / 2);
        } else {
            target.x = this.scalePixels(distance + (1920 - this.getWidth(target)) / 2);
        }
    }


    /**
     * Vertically align top a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in pixels
     * @param src - display object or rectangle
     */
    public verticalAlignTop(target: DisplayObject, distance: number = 0, src?: Container | Rectangle) {
        // if (src == undefined) {
        //     src = this.rect;
        // }
        // target.y = src.y + src.height * (distance) / 100;
        if (src) {
            target.y = this.scalePixels(this.getY(src) + this.getHeight(src) * distance);
        } else {
            target.y = this.scalePixels(distance);
        }
    }


    /**
     * Vertically align middle a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in pixels
     * @param src - display object or rectangle
     */
    public verticalAlignMiddle(target: Sprite | Container, distance: number = 0, src?: Sprite | Container | Rectangle) {
        // if (src == undefined) {
        //     src = this.rect;
        // } else if (src == target.parent && src instanceof Sprite) {
        //     src = new Rectangle(0, 0, src.width / src.scale.x, src.height / src.scale.y);
        // }
        //
        // const aY = target instanceof Sprite ? target.anchor.y : 0;
        //
        // target.y = src.y + src.height * (50 + distance) / 100 - target.height * (.5 - aY);
        if (src) {
            target.y = this.scalePixels(distance + this.getY(src) + (this.getHeight(src) - this.getHeight(target)) / 2);
        } else {
            target.y = this.scalePixels(distance + (1080 - this.getHeight(target)) / 2);
        }
    }


    /**
     * Vertically align bottom a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in pixels
     * @param src - display object or rectangle
     */
    // TODO add possibility to align depending on landscape/portrait mode/ Currently only works for landscape
    public verticalAlignBottom(target: Container, distance: number = 0, src?: Container | Rectangle) {
        // if (src == undefined) {
        //     src = this.rect;
        // }
        // target.y = src.y + src.height * (100 - distance) / 100 - target.height;
        if (src) {
            target.y = this.scalePixels(this.getY(src) + this.getHeight(src) - this.getHeight(target) - distance);
        } else {
            target.y = this.scalePixels(1080 - this.getHeight(target) - distance);
        }
    }

    /**
     * Return the coordinates of a global point as percentage of the layout rectangle
     * @public
     * @param globalPt - global point
     * @return {Point}
     */
    public getRelCoords(globalPt: Point) {
        const relX = globalPt.x * 100 / this.deviceWidth;
        const relY = globalPt.y * 100 / this.deviceHeight;

        const rect = this.landscape ? this.rect : this.portraitRect;

        const ptX = relX / 100 * rect.width;
        const ptY = relY / 100 * rect.height;

        return new Point(ptX, ptY);
    }


    /**
     * Apply layout to children of a container spacing them equally on the vertical
     * @public
     * @param container
     * @param vStep - the distance between the children
     */
    public applyVerticalLayout(container: Container, vStep: number) {
        const numChildren = container.children.length;
        let totalHeight = 0;
        const rect = new Rectangle(0, 0, container.width, container.height);
        for (let childIdx = 0; childIdx < numChildren; childIdx++) {
            const child = container.getChildAt(childIdx) as Container;
            this.horizontalAlignCenter(child, 0, rect);
            child.y = totalHeight;
            totalHeight += (child.height + vStep);
        }
    }


    /**
     * Apply layout to children of a container spacing them equally on the horizontal
     * @public
     * @param container
     * @param hStep - the distance between the children
     */
    public applyHorizontalLayout(container: Container, hStep: number) {
        const numChildren = container.children.length;
        let totalWidth = 0;
        const rect = new Rectangle(0, 0, container.width, container.height);
        for (let childIdx = 0; childIdx < numChildren; childIdx++) {
            const child = container.getChildAt(childIdx) as Container;
            child.x = totalWidth;
            this.verticalAlignMiddle(child, 0, rect);
            totalWidth += (child.width + hStep);
        }
    }


    /**
     * Set size and position of an object using a rectangle as coordinate system.
     * Aspect is not preserved
     * @param object - object to position and size
     * @param x - x coord
     * @param y - y coord
     * @param w - width
     * @param h - height
     * @param rect - source rectangle
     */
    public setToRectangle(object: Container, x: number, y: number, w: number, h: number, rect?: Container | Rectangle) {
        rect = rect == undefined ? this.rect : rect;

        object.x = rect.x + x / 100 * rect.width;
        object.y = rect.y + y / 100 * rect.height;
        object.width = w / 100 * rect.width;
        object.height = h / 100 * rect.height;
    }


    // TODO Fix types
    public putInRect(object: any, rect: IBoundsObject | Container | Rectangle, src?: any) {
        let width: number;
        let height: number;
        if (src == object.parent) {
            width = Math.abs(src.width / src.scale.x);
            height = Math.abs(src.height / src.scale.y);
            let anchor;
            if (src.anchor) {
                anchor = {x: src.anchor.x, y: src.anchor.y};
            } else {
                anchor = {x: 0, y: 0};
            }
            src = new Rectangle(0, 0, width, height);
            src.anchor = anchor;
        } else {
            width = Math.abs(src.width);
            height = Math.abs(src.height);
        }

        const modRect = new Rectangle();
        modRect.x = src.x + rect.x / 100 * width;
        modRect.y = src.y + rect.y / 100 * height;

        if (src.anchor) {
            modRect.x -= width * src.anchor.x;
            modRect.y -= height * src.anchor.y;
        }

        modRect.width = width * rect.width / 100;
        modRect.height = height * rect.height / 100;

        if (object.scale) {
            object.scale.set(1);
        }

        if (object.width / object.height > modRect.width / modRect.height) {
            const scale = modRect.width / object.width;
            object.scale.set(scale, scale);
            object.x = modRect.x;
            object.y = modRect.y + (modRect.height - object.height) * .5;
        } else {
            const scale = modRect.height / object.height;
            object.scale.set(scale, scale);
            object.x = modRect.x + (modRect.width - object.width) * .5;
            object.y = modRect.y;
        }

        if (object.anchor) {
            object.x += object.anchor.x * object.width;
            object.y += object.anchor.y * object.height;

        }
    }


    /**
     * Limit the size of an object. Preserves aspect
     * @param object
     * @param w - max width
     * @param h - max height
     * @param rect - source rect to use for coord system
     */
    public limitSize(object: Container, w: number, h: number, rect?: Container | Rectangle | Texture) {
        if (rect === undefined) {
            rect = this.rect;
        }

        if (object.scale) {
            object.scale.set(1);
        }

        const scale = Math.min(1, Math.min(this.relWidth(w, rect) / object.width, this.relHeight(h, rect) / object.height));
        object.scale.set(scale, scale);

    }

    public group(targets: PIXIElement | PIXIElement[], containerName: string = "tempContainer"): PIXIElement {
        if (targets === null) {
            throw new Error("Can't group a null or undefined object");
        }

        if ((targets as any).length) {
            targets = [...(targets as PIXIElement[])];
            const parent = targets[0].parent;
            const container = new Container();
            let groupDepth = 0;

            if (parent) {
                const origDepth = parent.getChildIndex(targets[0]);
                groupDepth = origDepth;

                for (const target of targets) {
                    if (parent.getChildIndex(target) < origDepth) {
                        groupDepth--;
                    }
                }
            }

            for (const target of targets) {
                container.addChild(target);
            }

            const bounds = container.getBounds();

            container.x = bounds.x;
            container.y = bounds.y;

            for (const target of targets) {
                target.x -= bounds.x;
                target.y -= bounds.y;
            }

            container.name = containerName;

            if (parent) {
                parent.addChildAt(container, groupDepth);
            }

            return container;
        } else {
            return targets as PIXIElement;
        }
    }

    public ungroup(container: Container, containerName: string = "tempContainer") {
        if (container && container.name === containerName) {

            const children = [...container.children];
            for (const child of children) {
                child.x += container.x - container.pivot.x;
                child.y += container.y - container.pivot.y;
                container.parent.addChild(child);
            }

            container.parent.removeChild(container);
            container.destroy();
        }
    }

    public centerPivot(targets: PIXIElement | PIXIElement[], containerName?: string, pivotPoint?: Point): PIXIElement {
        if (!containerName) {
            containerName = "tempContainer";
        }

        const target = this.group(targets, containerName);

        if (pivotPoint === undefined) {
            pivotPoint = new Point(0.5, 0.5);
        }

        const resizble = target as any;
        if ((target as any).anchor) {
            const anchorable = target as any;
            if (anchorable.anchor.x !== pivotPoint.x || anchorable.anchor.y !== pivotPoint.y) {
                anchorable.anchor.set(pivotPoint.x, pivotPoint.y);
                target.x += resizble.width * pivotPoint.x;
                target.y += resizble.height * pivotPoint.y;
            }
        } else {
            if (target.pivot.x === 0) {
                target.pivot.set(resizble.width * pivotPoint.x, resizble.height * pivotPoint.y);
                target.x += resizble.width * pivotPoint.x;
                target.y += resizble.height * pivotPoint.y;
            }
        }

        return target;
    }

    public resetPivot(target: Container | Sprite, containerName: string = "tempContainer") {
        if ((target as any).anchor) {
            const anchorable = target as any;
            target.x -= target.width * anchorable.anchor.x;
            target.y -= target.height * anchorable.anchor.y;
            anchorable.anchor.set(0);
        } else {
            target.x -= target.pivot.x;
            target.y -= target.pivot.y;
            target.pivot.set(0, 0);
        }
    }

}

export interface IBoundsObject {
    x: number;
    y: number;
    width: number;
    height: number;
}

export default new Layout();
